import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Sprout, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppDialog } from "@/components/common/AppDialog";
import { useAuth } from "@/hooks/useAuth";
import {
  maintenanceApi,
  type DemoOrgSeedResponse,
  type DemoOrgSeedSummary,
} from "@/lib/api/services/maintenance.api";
import { getApiErrorMessage } from "@/lib/api/api";

/**
 * Support → Cleanup → Seed demo organization.
 *
 * The purge's counterpart: name an organization and get a working tenant — two
 * concepts, each with its own region, locations, recipes and menu, over one
 * shared ingredient catalogue. The kitchen's book is transcribed from a real
 * kitchen's prep book; the café's is its own. Content only; no users or roles
 * are created, so seeding grants nobody access they did not already have.
 *
 * The one thing that can go wrong is a name whose slug is already taken, which
 * the API rejects. Preview reports that collision instead of erroring, so this
 * card previews before it lets the operator commit — and treats a preview of a
 * *different* name as no preview at all, since editing the field invalidates
 * everything the panel is showing.
 */

const SUMMARY_LABELS: Array<{ key: keyof DemoOrgSeedSummary; label: MessageDescriptor }> = [
  { key: "companies", label: msg`Concepts` },
  { key: "regions", label: msg`Regions` },
  { key: "locations", label: msg`Locations` },
  { key: "materials", label: msg`Ingredients` },
  { key: "recipes", label: msg`Recipes` },
  { key: "recipeIngredients", label: msg`Recipe lines` },
  { key: "storageContainers", label: msg`Containers` },
  { key: "tools", label: msg`Tools` },
  { key: "menus", label: msg`Menus` },
  { key: "menuItems", label: msg`Menu items` },
];

function SummaryGrid({ summary }: { summary: DemoOrgSeedSummary }) {
  const { t } = useLingui();
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
      {SUMMARY_LABELS.map(({ key, label }) => (
        <div key={key} className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-(--color-text-secondary)">{t(label)}</span>
          <span className="font-medium tabular-nums">{summary[key]}</span>
        </div>
      ))}
    </div>
  );
}

function RecipeList({ preview }: { preview: DemoOrgSeedResponse }) {
  const { t } = useLingui();

  return (
    <ul className="divide-y divide-(--color-border) text-sm">
      {preview.details.recipes.map((recipe) => (
        <li key={recipe.name} className="flex flex-wrap items-baseline gap-x-3 py-1.5">
          <span className="font-medium">{recipe.name}</span>
          <span className="text-xs text-(--color-text-secondary)">
            {recipe.yield ? `${recipe.yield} · ` : ""}
            {t`${recipe.ingredients} ingredients`} · {t`${recipe.steps} steps`} ·{" "}
            {t`${recipe.shelfLifeDays} day shelf life`}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function SeedDemoOrgCard() {
  const { t } = useLingui();
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [preview, setPreview] = useState<DemoOrgSeedResponse | null>(null);
  /** The name `preview` describes. Anything else means the panel is stale. */
  const [previewedName, setPreviewedName] = useState("");
  const [result, setResult] = useState<DemoOrgSeedResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Both endpoints are gated on the wildcard permission, which SUPPORT does not
  // hold — so the card says so rather than offering buttons that 403.
  const canSeed = hasRole("SUPER_ADMIN");

  const trimmedName = name.trim();
  const nameIsUsable = trimmedName.length >= 2 && /[A-Za-z0-9]/.test(trimmedName);
  const previewIsCurrent = preview != null && previewedName === trimmedName;

  const previewMutation = useMutation({
    mutationFn: (organizationName: string) =>
      maintenanceApi.previewDemoOrgSeed({ organizationName }),
    onSuccess: (response) => {
      setPreview(response);
      setPreviewedName(response.organizationName);
      setResult(null);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to load preview`)),
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      maintenanceApi.applyDemoOrgSeed({ organizationName: trimmedName, confirm: true }),
    onSuccess: (response) => {
      setResult(response);
      setPreview(null);
      setPreviewedName("");
      setName("");
      setConfirmOpen(false);
      // A new organization changes what the org switcher and every scoped list
      // should be showing.
      void queryClient.invalidateQueries();
      toast.success(t`Created ${response.organizationName}`);
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, t`Seeding failed — nothing was created`)),
  });

  const openConfirm = () => {
    setConfirmOpen(true);
    // The dialog restates what is about to be created, and the slug collision
    // check lives in the preview — so fetch it if the operator skipped it.
    if (!previewIsCurrent && !previewMutation.isPending) previewMutation.mutate(trimmedName);
  };

  const conflict = previewIsCurrent ? preview.conflict : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sprout className="size-5 text-(--color-success)" />
          {t`Seed a demo organization`}
        </CardTitle>
        <CardDescription>
          {t`Creates a named organization pre-loaded with two concepts — a kitchen with three locations, ten prep recipes transcribed from a working kitchen's prep book, ten plated dishes built on them and a ten-item menu, and a café with two locations, eight recipes and a four-item menu — over one shared ingredient catalogue. Content only — no users, roles or role assignments are created.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canSeed ? (
          <p className="text-sm text-(--color-text-secondary)">
            {t`Only an administrator with full access can seed an organization.`}
          </p>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="seed-org-name">{t`Organization name`}</Label>
              <Input
                id="seed-org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t`Blue Ridge Hospitality`}
                autoComplete="off"
                disabled={applyMutation.isPending}
              />
              {previewIsCurrent && (
                <p className="text-xs text-(--color-text-secondary)">
                  {t`Slug: ${preview.slug}`}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={!nameIsUsable || previewMutation.isPending}
                onClick={() => previewMutation.mutate(trimmedName)}
              >
                {previewMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                {t`Preview`}
              </Button>
              <Button
                onClick={openConfirm}
                disabled={!nameIsUsable || conflict != null || applyMutation.isPending}
                className="gap-2"
              >
                <Sprout className="size-4" />
                {t`Create organization…`}
              </Button>
            </div>

            {conflict && (
              <p className="flex items-start gap-2 text-sm text-(--color-danger)">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                {t`"${conflict.name}" already uses the slug ${conflict.slug}. Pick a different name.`}
              </p>
            )}

            {previewIsCurrent && !conflict && (
              <div className="space-y-3 rounded-md border border-(--color-border) p-4">
                <p className="text-sm font-medium">{t`This would create:`}</p>
                <SummaryGrid summary={preview.summary} />
                <RecipeList preview={preview} />
                <p className="text-xs text-(--color-text-secondary)">{preview.details.note}</p>
              </div>
            )}

            {result && (
              <div className="space-y-3 rounded-md border border-(--color-border) bg-(--color-surface-muted)/50 p-4">
                <p className="text-sm font-medium">
                  {t`Created ${result.organizationName} (${result.slug}).`}
                </p>
                <SummaryGrid summary={result.summary} />
              </div>
            )}
          </>
        )}
      </CardContent>

      <AppDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!applyMutation.isPending) setConfirmOpen(open);
        }}
        title={t`Create ${trimmedName}?`}
        description={t`A new organization is created with demo content. Nobody is granted access to it — assign roles the usual way afterwards.`}
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={applyMutation.isPending}
            >
              {t`Cancel`}
            </Button>
            <Button
              disabled={conflict != null || applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
              className="gap-2"
            >
              {applyMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Sprout className="size-4" />
              )}
              {t`Create organization`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {conflict ? (
            <p className="flex items-start gap-2 text-sm text-(--color-danger)">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              {t`"${conflict.name}" already uses the slug ${conflict.slug}. Pick a different name.`}
            </p>
          ) : previewIsCurrent ? (
            <div className="rounded-md border border-(--color-border) p-3">
              <SummaryGrid summary={preview.summary} />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
              <Loader2 className="size-4 animate-spin" />
              {t`Checking the name and counting what would be created…`}
            </div>
          )}
        </div>
      </AppDialog>
    </Card>
  );
}
